/**
 * ZITELOU - Sidebar
 * Menu latéral de navigation
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  CreditCardIcon,
  ChartBarIcon,
  GiftIcon,
  Cog6ToothIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useAuth();

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: HomeIcon,
      roles: ['super_admin', 'admin', 'support'],
    },
    {
      name: 'Utilisateurs',
      path: '/users',
      icon: UsersIcon,
      roles: ['super_admin', 'admin', 'support'],
    },
    {
      name: 'Abonnements',
      path: '/subscriptions',
      icon: CreditCardIcon,
      roles: ['super_admin', 'admin', 'support'],
    },
    {
      name: 'Parrainage',
      path: '/referrals',
      icon: GiftIcon,
      roles: ['super_admin', 'admin'],
    },
    {
      name: 'Statistiques',
      path: '/statistics',
      icon: ChartBarIcon,
      roles: ['super_admin', 'admin', 'support'],
    },
    {
      name: 'Configuration',
      path: '/config',
      icon: Cog6ToothIcon,
      roles: ['super_admin', 'admin'],
    },
  ];

  // Filter menu items by user role
  const visibleMenuItems = menuItems.filter((item) =>
    item.roles.includes(user?.role)
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={onClose}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-30
          w-64 bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col
        `}
      >
        {/* Close button (mobile) */}
        <div className="lg:hidden flex justify-end p-4">
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Logo (Desktop) */}
        <div className="hidden lg:flex items-center gap-3 px-6 py-6 border-b border-gray-200">
          <div className="flex flex-col gap-1">
              <strong>Zitelou</strong>
            <p className="text-xs text-gray-500">Back Office</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {visibleMenuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={`w-5 h-5 ${
                      isActive ? 'text-primary' : 'text-gray-500'
                    }`}
                  />
                  <span>{item.name}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
              <span className="text-gray-600 text-sm font-medium">
                {user?.firstname?.charAt(0)}
                {user?.lastname?.charAt(0)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.firstname} {user?.lastname}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
